'use client';

import { useState } from 'react';

export default function NewsletterSignup() {
  const [email, setEmail] = useState('');
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    // Simulate API call
    setTimeout(() => {
      setIsSubscribed(true);
      setIsLoading(false);
      setEmail('');
    }, 1000);
  };

  if (isSubscribed) {
    return (
      <section className="py-12 sm:py-16 lg:py-20 bg-gradient-to-r from-green-600 to-green-800">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <div className="w-12 h-12 sm:w-16 sm:h-16 flex items-center justify-center bg-white rounded-full mx-auto mb-4 sm:mb-6">
            <i className="ri-check-line text-green-600 text-xl sm:text-2xl"></i>
          </div>
          <h2 className="text-2xl sm:text-3xl font-bold text-white mb-3 sm:mb-4">
            Welcome to Our Wellness Community!
          </h2>
          <p className="text-green-100 text-base sm:text-lg">
            Thank you for subscribing. You'll receive our latest health tips and exclusive offers soon.
          </p>
        </div>
      </section>
    );
  }

  return (
    <section 
      className="py-12 sm:py-16 lg:py-20 relative"
      style={{
        backgroundImage: `url('https://readdy.ai/api/search-image?query=Beautiful%20ayurvedic%20newsletter%20background%20with%20natural%20herbs%2C%20green%20leaves%2C%20lotus%20flowers%2C%20soft%20natural%20lighting%2C%20wellness%20spa%20environment%2C%20serene%20atmosphere%2C%20organic%20textures%2C%20gradient%20overlay%20from%20green%20to%20teal%2C%20professional%20photography%20style%2C%20peaceful%20meditation%20setting&width=1920&height=600&seq=newsletter1&orientation=landscape')`,
        backgroundSize: 'cover',
        backgroundPosition: 'center'
      }}
    >
      <div className="absolute inset-0 bg-gradient-to-r from-green-600/90 to-green-800/90"></div>
      
      <div className="relative max-w-4xl mx-auto px-4 sm:px-6 text-center">
        <div className="w-12 h-12 sm:w-16 sm:h-16 flex items-center justify-center bg-white rounded-full mx-auto mb-4 sm:mb-6">
          <i className="ri-mail-line text-green-600 text-xl sm:text-2xl"></i>
        </div>
        
        <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-3 sm:mb-4">
          Stay Connected to Wellness
        </h2>
        
        <p className="text-green-100 text-sm sm:text-base lg:text-lg mb-6 sm:mb-8 max-w-2xl mx-auto px-4">
          Subscribe to our newsletter and receive exclusive health tips, Ayurvedic wisdom, 
          special offers, and be the first to know about new products.
        </p>

        <form onSubmit={handleSubmit} className="max-w-md mx-auto mb-6 sm:mb-8">
          <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Your email address"
              required
              className="flex-1 px-4 py-2.5 sm:py-3 rounded-full text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-white/40 text-sm sm:text-base border-0 shadow-sm"
            />
            <button
              type="submit"
              disabled={isLoading}
              className="bg-white text-green-600 px-5 sm:px-6 py-2.5 sm:py-3 rounded-full font-medium hover:bg-green-50 transition-all duration-200 whitespace-nowrap disabled:opacity-50 text-sm sm:text-base shadow-sm hover:shadow-md"
            >
              {isLoading ? (
                <div className="flex items-center justify-center space-x-2">
                  <div className="w-3 h-3 sm:w-4 sm:h-4 border-2 border-green-600 border-t-transparent rounded-full animate-spin"></div>
                  <span>Subscribing...</span>
                </div>
              ) : (
                'Subscribe'
              )}
            </button>
          </div>
        </form>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 sm:gap-8 max-w-4xl mx-auto">
          <div className="flex flex-col sm:flex-row items-center sm:items-start space-y-3 sm:space-y-0 sm:space-x-4 text-white bg-white/10 p-4 sm:p-3 rounded-2xl sm:rounded-none sm:bg-transparent">
            <div className="w-12 h-12 sm:w-10 sm:h-10 flex items-center justify-center bg-white/20 rounded-full flex-shrink-0">
              <i className="ri-gift-line text-white text-lg sm:text-base"></i>
            </div>
            <div className="text-center sm:text-left">
              <h4 className="font-semibold text-base sm:text-sm lg:text-base mb-1">Exclusive Offers</h4>
              <p className="text-green-100 text-sm sm:text-xs lg:text-sm">Up to 30% off for subscribers</p>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row items-center sm:items-start space-y-3 sm:space-y-0 sm:space-x-4 text-white bg-white/10 p-4 sm:p-3 rounded-2xl sm:rounded-none sm:bg-transparent">
            <div className="w-12 h-12 sm:w-10 sm:h-10 flex items-center justify-center bg-white/20 rounded-full flex-shrink-0">
              <i className="ri-lightbulb-line text-white text-lg sm:text-base"></i>
            </div>
            <div className="text-center sm:text-left">
              <h4 className="font-semibold text-base sm:text-sm lg:text-base mb-1">Health Tips</h4>
              <p className="text-green-100 text-sm sm:text-xs lg:text-sm">Weekly wellness insights</p>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row items-center sm:items-start space-y-3 sm:space-y-0 sm:space-x-4 text-white bg-white/10 p-4 sm:p-3 rounded-2xl sm:rounded-none sm:bg-transparent">
            <div className="w-12 h-12 sm:w-10 sm:h-10 flex items-center justify-center bg-white/20 rounded-full flex-shrink-0">
              <i className="ri-star-line text-white text-lg sm:text-base"></i>
            </div>
            <div className="text-center sm:text-left">
              <h4 className="font-semibold text-base sm:text-sm lg:text-base mb-1">Early Access</h4>
              <p className="text-green-100 text-sm sm:text-xs lg:text-sm">New products first</p>
            </div>
          </div>
        </div>

        <p className="text-green-200 text-xs sm:text-sm mt-6 sm:mt-8">
          We respect your privacy. Unsubscribe at any time.
        </p>
      </div>
    </section>
  );
}